import React, { useEffect, useRef } from 'react';

interface QuestionNavigatorProps {
  children: React.ReactNode;
  isActive: boolean;
  onAnimationComplete?: () => void;
}

const QuestionNavigator: React.FC<QuestionNavigatorProps> = ({ 
  children, 
  isActive,
  onAnimationComplete
}) => {
  const questionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && questionRef.current) {
      // Scroll the question into view with smooth animation
      const scrollOptions: ScrollIntoViewOptions = {
        behavior: 'smooth',
        block: 'center',
      };
      
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        questionRef.current?.scrollIntoView(scrollOptions);
        if (onAnimationComplete) onAnimationComplete();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, onAnimationComplete]);

  // Handle keyboard appearance on mobile
  useEffect(() => {
    const handleResize = () => {
      if (isActive && questionRef.current) {
        // Check if this is likely a keyboard appearance (height significantly reduced)
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.clientHeight;
        
        if (windowHeight < documentHeight * 0.8) {
          // Keyboard likely appeared, adjust scroll position
          questionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive]);

  return (
    <div 
      ref={questionRef}
      className={`transition-all duration-500 ease-in-out ${
        isActive 
          ? 'opacity-100 transform translate-y-0' 
          : 'opacity-0 transform translate-y-8 pointer-events-none absolute'
      }`}
      style={{ 
        minHeight: isActive ? 'auto' : '0',
        overflow: isActive ? 'visible' : 'hidden',
        position: isActive ? 'relative' : 'absolute',
        width: '100%'
      }}
    >
      {children}
    </div>
  );
};

export default QuestionNavigator;